import toast from 'react-hot-toast'
import useAppStore from '../store/useAppStore'
import useCasesStore from '../store/useCasesStore'
import useInventoryStore from '../store/useInventoryStore'
import useUpgraderStore from '../store/useUpgraderStore'
import useContractStore from '../store/useContractStore'
import { isDevelopmentMode, generateMockInitData, DEFAULT_MOCK_USER } from '../utils/mockData'

const WEB_AUTH_TOKEN_COOKIE = 'skinoz_web_auth_token'
const WEB_AUTH_UID_COOKIE = 'skinoz_web_auth_uid'
const WEB_AUTH_COOKIE_MAX_AGE = 60 * 60 * 24 * 365 * 10
const AUTH_REF_COOKIE = 'skinoz_auth_ref'
const AUTH_REF_COOKIE_MAX_AGE = 60 * 60 * 24 * 365
const RUNTIME_ENVIRONMENT_STORAGE_KEY = 'skinoz_runtime_environment'

/**
 * API Service for SKINOZ Backend Integration
 * Handles unified API responses with multiple keys (cases, me, alert, error)
 * Implements load balancing across subdomains based on Telegram user ID
 */

class ApiService {
  constructor() {
    this.baseUrl = import.meta.env.VITE_API_BASE || 'skinshub.ru'
    this.protocol = 'https'
    this.currentSubdomain = null
  }

  getCookie(name) {
    if (typeof document === 'undefined') {
      return null
    }

    const escapedName = name.replace(/[.*+?^${}()|[\]\\]/g, '\\$&')
    const match = document.cookie.match(new RegExp(`(?:^|; )${escapedName}=([^;]*)`))

    return match ? decodeURIComponent(match[1]) : null
  }

  setCookie(name, value, maxAge = WEB_AUTH_COOKIE_MAX_AGE) {
    if (typeof document === 'undefined') {
      return
    }

    document.cookie = `${name}=${encodeURIComponent(value)}; path=/; max-age=${maxAge}; SameSite=Lax`
  }

  removeCookie(name) {
    if (typeof document === 'undefined') {
      return
    }

    document.cookie = `${name}=; path=/; max-age=0; SameSite=Lax`
  }

  isValidWebAuthUid(uid) {
    return typeof uid === 'string' && /^\d+$/.test(uid)
  }

  getStoredWebAuth() {
    const token = this.getCookie(WEB_AUTH_TOKEN_COOKIE)
    const uid = this.getCookie(WEB_AUTH_UID_COOKIE)

    if (!token || !uid || !this.isValidWebAuthUid(uid)) {
      return null
    }

    return {
      token,
      uid,
      userId: Number(uid)
    }
  }

  saveWebAuth(token, uid) {
    this.setCookie(WEB_AUTH_TOKEN_COOKIE, token)
    this.setCookie(WEB_AUTH_UID_COOKIE, uid)
  }

  clearWebAuth() {
    this.removeCookie(WEB_AUTH_TOKEN_COOKIE)
    this.removeCookie(WEB_AUTH_UID_COOKIE)
    this.currentSubdomain = null
  }

  getStoredAuthRef() {
    return this.getCookie(AUTH_REF_COOKIE)
  }

  saveAuthRef(ref) {
    this.setCookie(AUTH_REF_COOKIE, ref, AUTH_REF_COOKIE_MAX_AGE)
  }

  clearStoredAuthRef() {
    this.removeCookie(AUTH_REF_COOKIE)
  }

  captureAuthRefFromUrl() {
    if (typeof window === 'undefined') {
      return null
    }

    const currentUrl = new URL(window.location.href)
    const ref = currentUrl.searchParams.get('ref')

    if (ref === null) {
      return this.getStoredAuthRef()
    }

    this.saveAuthRef(ref)
    console.log('✅ Auth ref сохранен из URL:', ref)

    return ref
  }

  getAuthBaseUrl() {
    return `https://auth.${this.baseUrl}`
  }

  getAuthLoginUrl(provider) {
    const authUrl = new URL(this.getAuthBaseUrl())
    const authRef = this.captureAuthRefFromUrl()

    authUrl.searchParams.set('action', 'login')
    authUrl.searchParams.set('provider', provider)

    if (authRef !== null) {
      authUrl.searchParams.set('ref', authRef)
    }

    return authUrl.toString()
  }

  getTelegramLaunchParams() {
    if (typeof window === 'undefined') {
      return {
        initData: null,
        platform: null,
        version: null
      }
    }

    const currentUrl = new URL(window.location.href)
    const hashValue = currentUrl.hash.startsWith('#')
      ? currentUrl.hash.slice(1)
      : currentUrl.hash
    const hashParams = new URLSearchParams(hashValue)
    const getLaunchParam = (name) => {
      return currentUrl.searchParams.get(name)
        || hashParams.get(name)
        || null
    }

    // Предпочитаем свойства из SDK (window.Telegram.WebApp) — они стабильны и
    // не пропадают при history.replaceState (например, после deep-link navigate).
    // URL-параметры (tgWebApp*) оставляем как fallback на случай, если SDK ещё не
    // успел проинициализироваться. Значение platform === 'unknown' означает,
    // что приложение открыто вне Telegram (обычный браузер), его игнорируем.
    const tg = window.Telegram?.WebApp
    const sdkPlatform = tg?.platform && tg.platform !== 'unknown' ? tg.platform : null
    const sdkVersion = tg?.version || null

    return {
      initData: tg?.initData || getLaunchParam('tgWebAppData'),
      platform: sdkPlatform || getLaunchParam('tgWebAppPlatform'),
      version: sdkVersion || getLaunchParam('tgWebAppVersion')
    }
  }

  getRuntimeEnvironment() {
    const telegramLaunchParams = this.getTelegramLaunchParams()
    const hasTelegramLaunchSignature = Boolean(
      telegramLaunchParams.initData
      && telegramLaunchParams.platform
      && telegramLaunchParams.version
    )

    if (typeof window === 'undefined') {
      return hasTelegramLaunchSignature ? 'telegram' : 'browser'
    }

    if (hasTelegramLaunchSignature) {
      window.sessionStorage.setItem(RUNTIME_ENVIRONMENT_STORAGE_KEY, 'telegram')
      return 'telegram'
    }

    const storedRuntimeEnvironment = window.sessionStorage.getItem(RUNTIME_ENVIRONMENT_STORAGE_KEY)
    return storedRuntimeEnvironment === 'telegram' ? 'telegram' : 'browser'
  }

  hasTelegramInitData() {
    return Boolean(this.getTelegramLaunchParams().initData)
  }

  clearTelegramBrowserSession() {
    if (typeof window === 'undefined') {
      return
    }

    try {
      window.sessionStorage.removeItem('__telegram__initParams')
      window.sessionStorage.removeItem(RUNTIME_ENVIRONMENT_STORAGE_KEY)
    } catch (error) {
      console.warn('⚠️ Не удалось очистить sessionStorage Telegram init params:', error)
    }

    const currentUrl = new URL(window.location.href)
    const hashValue = currentUrl.hash.startsWith('#')
      ? currentUrl.hash.slice(1)
      : currentUrl.hash
    const hashParams = new URLSearchParams(hashValue)
    const telegramLaunchKeys = [
      'tgWebAppData',
      'tgWebAppVersion',
      'tgWebAppPlatform',
      'tgWebAppThemeParams',
      'tgWebAppStartParam',
      'tgWebAppBotInline',
      'tgWebAppShowSettings',
      'tgWebAppFullscreen',
      'tgWebAppDefaultColors'
    ]

    telegramLaunchKeys.forEach((key) => {
      currentUrl.searchParams.delete(key)
      hashParams.delete(key)
    })

    const nextHash = hashParams.toString()
    const nextUrl = `${currentUrl.pathname}${currentUrl.search}${nextHash ? `#${nextHash}` : ''}`
    window.history.replaceState({}, document.title, nextUrl)
  }

  clearBrowserAuthState() {
    this.clearWebAuth()

    if (this.getRuntimeEnvironment() !== 'telegram') {
      this.clearTelegramBrowserSession()
    }
  }

  cleanupAuthParamsFromUrl(redirectToRoot = false) {
    if (typeof window === 'undefined') {
      return
    }

    const currentUrl = new URL(window.location.href)
    currentUrl.searchParams.delete('token')
    currentUrl.searchParams.delete('uid')

    const targetUrl = redirectToRoot
      ? `${currentUrl.origin}/`
      : `${currentUrl.pathname}${currentUrl.search}${currentUrl.hash}`

    window.history.replaceState({}, document.title, targetUrl)
  }

  initializeWebAuthFromUrl() {
    if (typeof window === 'undefined') {
      return { hasNewAuth: false, isInvalid: false }
    }

    this.captureAuthRefFromUrl()

    if (this.hasTelegramInitData()) {
      this.cleanupAuthParamsFromUrl(false)
      return { hasNewAuth: false, isInvalid: false }
    }

    const url = new URL(window.location.href)
    const token = url.searchParams.get('token')
    const uid = url.searchParams.get('uid')
    const hasAuthParams = token !== null || uid !== null

    if (!hasAuthParams) {
      return { hasNewAuth: false, isInvalid: false }
    }

    if (!token || !uid || !this.isValidWebAuthUid(uid)) {
      console.warn('⚠️ Получены некорректные параметры web-авторизации')
      this.clearWebAuth()
      this.cleanupAuthParamsFromUrl(true)
      return { hasNewAuth: false, isInvalid: true }
    }

    this.saveWebAuth(token, uid)
    this.clearStoredAuthRef()
    this.cleanupAuthParamsFromUrl(true)
    console.log('✅ Данные web-авторизации сохранены из URL')

    return {
      hasNewAuth: true,
      isInvalid: false,
      token,
      uid
    }
  }

  getAuthContext() {
    const telegramInitData = this.getTelegramInitData({ suppressWarnings: true })
    const runtimeEnvironment = this.getRuntimeEnvironment()

    if (telegramInitData) {
      return {
        isAuthorized: true,
        mode: 'telegram',
        runtimeEnvironment,
        token: telegramInitData,
        userId: window.Telegram?.WebApp?.initDataUnsafe?.user?.id || null
      }
    }

    const storedWebAuth = this.getStoredWebAuth()
    if (storedWebAuth) {
      return {
        isAuthorized: true,
        mode: 'web',
        runtimeEnvironment,
        token: storedWebAuth.token,
        userId: storedWebAuth.userId,
        uid: storedWebAuth.uid
      }
    }

    if (isDevelopmentMode()) {
      return {
        isAuthorized: true,
        mode: 'development',
        runtimeEnvironment,
        token: generateMockInitData(),
        userId: DEFAULT_MOCK_USER.id
      }
    }

    return {
      isAuthorized: false,
      mode: null,
      runtimeEnvironment,
      token: null,
      userId: null
    }
  }

  /**
   * Get the appropriate subdomain based on Telegram user ID
   * Uses last digit of user ID for load balancing (0-9)
   */
  getSubdomain() {
    const authContext = this.getAuthContext()
    const { telegramUser } = useAppStore.getState()

    if (authContext.mode === 'web' && authContext.userId !== null) {
      const lastDigit = authContext.userId % 10
      this.currentSubdomain = `${lastDigit}.${this.baseUrl}`
      return this.currentSubdomain
    }

    if (telegramUser?.id) {
      const lastDigit = telegramUser.id % 10
      this.currentSubdomain = `${lastDigit}.${this.baseUrl}`
      return this.currentSubdomain
    }

    if (authContext.userId !== null) {
      const lastDigit = authContext.userId % 10
      this.currentSubdomain = `${lastDigit}.${this.baseUrl}`
      return this.currentSubdomain
    }

    this.currentSubdomain = `0.${this.baseUrl}`

    return this.currentSubdomain
  }

  /**
   * Get full API URL with subdomain
   */
  getApiUrl(endpoint) {
    const subdomain = this.getSubdomain()
    return `${this.protocol}://${subdomain}${endpoint}`
  }

  /**
   * Initialize Telegram WebApp
   * Sets up WebApp configuration and validates environment
   */
  initializeTelegramWebApp() {
    if (isDevelopmentMode()) {
      console.log('🔧 Development mode: Skipping Telegram WebApp initialization')
      return true
    }
    
    if (!window.Telegram?.WebApp) {
      console.error('❌ Telegram WebApp не найден')
      return false
    }
    
    try {
      const tg = window.Telegram.WebApp
      
      // Проверяем версию WebApp
      if (!tg.isVersionAtLeast('6.0')) {
        console.warn('⚠️ Версия Telegram WebApp устарела, рекомендуется обновление')
      }
      
      // Настраиваем WebApp
      tg.ready()
      tg.expand()
      
      // Настраиваем тему
      tg.setHeaderColor('#0A0A0F')
      tg.setBackgroundColor('#0A0A0F')
      
      // Получаем данные пользователя
      const user = tg.initDataUnsafe?.user
      if (user) {
        const { setTelegramUser } = useAppStore.getState()
        setTelegramUser(user)
        console.log('✅ Telegram пользователь установлен:', user.first_name)
      }
      
      return true
    } catch (error) {
      console.error('❌ Ошибка инициализации Telegram WebApp:', error)
      return false
    }
  }

  /**
   * Get Telegram initData for authentication
   * Uses mock data for localhost development with validation
   */
  getTelegramInitData({ suppressWarnings = false } = {}) {
    // Check WebApp readiness
    if (!window.Telegram?.WebApp?.initData) {
      if (!suppressWarnings) {
        console.warn('⚠️ Telegram WebApp initData недоступен')
      }
      return null
    }
    
    const initData = window.Telegram.WebApp.initData
    
    // Validate initData
    if (initData.length < 10) {
      if (!suppressWarnings) {
        console.warn('⚠️ InitData кажется слишком коротким')
      }
      return null
    }
    
    // Check required fields
    const params = new URLSearchParams(initData)
    if (!params.get('user') || !params.get('auth_date')) {
      if (!suppressWarnings) {
        console.warn('⚠️ InitData не содержит обязательных полей')
      }
      return null
    }
    
    // Check auth_date freshness (not older than 24 hours)
    const authDate = parseInt(params.get('auth_date'))
    const now = Math.floor(Date.now() / 1000)
    if (now - authDate > 86400) {
      if (!suppressWarnings) {
        console.warn('⚠️ InitData устарел, рекомендуется перезапуск приложения')
      }
    }
    
    return initData
  }

  handleAuthorizationFailure(authContext, message = 'Ошибка авторизации. Выполните вход заново') {
    console.error('❌ Обнаружена проблема авторизации')
    this.clearBrowserAuthState()

    const appState = useAppStore.getState()

    if (appState?.setAuthState) {
      appState.setAuthState({
        isAuthorized: false,
        authMode: null,
        runtimeEnvironment: this.getRuntimeEnvironment()
      })
    }

    if (appState?.setUser) {
      appState.setUser(null)
    }

    if (appState?.setTelegramUser) {
      appState.setTelegramUser(null)
    }

    if (appState?.setIsFirstVisit) {
      appState.setIsFirstVisit(false)
    }

    toast.error(message)

    if (authContext.runtimeEnvironment === 'telegram' && window.Telegram?.WebApp?.close) {
      setTimeout(() => {
        window.Telegram.WebApp.close()
      }, 2000)
    }

    if (authContext.runtimeEnvironment === 'browser' && typeof window !== 'undefined') {
      window.location.replace('/welcome')
    }

    const authError = new Error('Ошибка авторизации')
    authError.isHandledAuthError = true
    throw authError
  }

  /**
   * Universal API request handler
   * Processes unified responses with multiple keys and enhanced error handling
   */
  async makeRequest(endpoint, options = {}) {
    try {
      const authContext = this.getAuthContext()
      const url = this.getApiUrl(endpoint)
      
      // Check authorization data availability
      if (!authContext.isAuthorized) {
        throw new Error('Нет данных авторизации')
      }
      
      // Log all API requests for debugging
      if (isDevelopmentMode()) {
        console.log('🌐 API Request:', url)
      }

      const defaultHeaders = {
        'Content-Type': 'application/json',
        'Accept': 'application/json',
      }

      // Add Telegram initData for authentication
      if (authContext.token) {
        defaultHeaders['X-Telegram-Init-Data'] = authContext.token
        if (isDevelopmentMode()) {
          console.log('🔑 Adding X-Telegram-Init-Data header', authContext.mode)
        }
      } else {
        console.warn('⚠️ No initData available for authentication')
      }

      const requestOptions = {
        method: options.method || 'GET',
        headers: { ...defaultHeaders, ...options.headers },
        ...options,
        // Убеждаемся, что headers не перезаписываются
        headers: { ...defaultHeaders, ...options.headers }
      }

      // Логируем заголовки для отладки
      if (isDevelopmentMode()) {
        console.log('📤 Request headers:', requestOptions.headers)
      }

      const response = await fetch(url, requestOptions)
      
      // Enhanced error handling for authorization
      if (response.status === 401) {
        console.error('❌ Ошибка авторизации (401)')
        this.handleAuthorizationFailure(authContext)
      }
      
      if (response.status === 403) {
        console.error('❌ Доступ запрещен (403)')
        toast.error('Доступ запрещен')
        throw new Error('Доступ запрещен')
      }
      
      if (!response.ok) {
        throw new Error(`HTTP ${response.status}: ${response.statusText}`)
      }

      const data = await response.json()

      if (data?.error_auth === true) {
        console.error('❌ Бэкенд вернул error_auth=true')
        this.handleAuthorizationFailure(authContext)
      }
      
      // Process unified response
      this.processUnifiedResponse(data)
      
      return data
    } catch (error) {
      if (error?.name === 'AbortError') {
        // Запрос отменен при размонтировании/смене страницы
        throw error
      }

      if (error?.isHandledAuthError) {
        throw error
      }

      console.error('❌ API Request failed:', error)

      // Enhanced error messaging
      if (error.message.includes('авторизации')) {
        toast.error('Проблема с авторизацией')
      } else if (error.message.includes('NetworkError') || error.message.includes('Failed to fetch')) {
        toast.error('Проблема с подключением к серверу')
      } else {
        toast.error(`Ошибка API: ${error.message}`)
      }

      throw error
    }
  }

  /**
   * Process unified API response with multiple keys
   * Updates appropriate Zustand stores based on response keys
   */
  processUnifiedResponse(data) {
    // Handle errors FIRST - если есть ошибка, не обрабатываем остальные ключи
    // НЕ показываем toast здесь - это делается в userApi для правильной локализации
    if (data.error) {
      return // Прерываем обработку при ошибке, но не показываем toast
    }

    // Handle user data and balance
    if (data.me) {
      const state = useAppStore.getState()
      const { setUser, setBalance, setTasks, setIsFirstVisit } = state
      
      // Обновляем пользователя только если данные изменились
      const currentUser = state.user
      if (JSON.stringify(currentUser) !== JSON.stringify(data.me)) {
        setUser(data.me)
      }
      
      // Обновляем баланс только если изменился
      const rawBalance = data.me.balance !== undefined ? data.me.balance : data.me.points
      const normalizedBalance = Number(rawBalance)
      const nextBalance = Number.isFinite(normalizedBalance) ? normalizedBalance : 0

      if (rawBalance !== undefined && nextBalance !== Number(state.balance || 0)) {
        setBalance(nextBalance)
      }
      
      // Обновляем задания только если изменились
      if (data.me.tasks && JSON.stringify(data.me.tasks) !== JSON.stringify(state.tasks)) {
        setTasks(data.me.tasks)
      }
      
      // Обновляем статус первого визита для онбординга только если значение изменилось
      if (data.me.is_first_visit !== undefined) {
        const currentIsFirstVisit = state.isFirstVisit
        if (currentIsFirstVisit !== data.me.is_first_visit) {
          console.log('🎯 API получил новое is_first_visit:', data.me.is_first_visit, 'было:', currentIsFirstVisit)
          setIsFirstVisit(data.me.is_first_visit)
          console.log('✅ setIsFirstVisit вызван с значением:', data.me.is_first_visit)
        } else {
          console.log('⏸️ is_first_visit не изменился:', data.me.is_first_visit)
        }
      } else {
        console.log('⚠️ is_first_visit не найден в ответе API')
      }
    }

    // Handle cases data
    if (data.cases) {
      const { setCases } = useCasesStore.getState()
      setCases(data.cases)
    }

    // Handle case details
    if (data.case) {
      const { setCurrentCase } = useCasesStore.getState()
      setCurrentCase(data.case)
    }

    // Handle inventory data
    if (data.inventory) {
      const { setItems } = useInventoryStore.getState()
      setItems(data.inventory)
    }

    // Handle case opening result
    if (data.opened_item) {
      const { setOpeningResult } = useCasesStore.getState()
      setOpeningResult(data.opened_item)
    }

    // Handle alerts (success messages)
    if (data.alert) {
      toast.success(data.alert)
    }

    // Handle notifications
    if (data.notification) {
      toast(data.notification, {
        icon: '🔔',
        style: {
          background: 'linear-gradient(135deg, #4EFFC6 0%, #00FF7F 100%)',
          color: '#0A0A0F',
        }
      })
    }

    // Handle balance updates (fallback для старых API)
    if (data.balance !== undefined && !data.me) {
      const { setBalance } = useAppStore.getState()
      const normalizedBalance = Number(data.balance)
      setBalance(Number.isFinite(normalizedBalance) ? normalizedBalance : 0)
    }

    // Handle tasks updates (fallback для старых API)
    if (data.tasks && !data.me) {
      const { setTasks } = useAppStore.getState()
      setTasks(data.tasks)
    }

    // Handle recent wins
    if (data.recent_wins) {
      const { setRecentWins } = useAppStore.getState()
      setRecentWins(data.recent_wins)
    }

    // Handle upgrader data (список доступных скинов для апгрейда)
    if (data.upgrader) {
      const { setAvailableItems } = useUpgraderStore.getState()
      setAvailableItems(data.upgrader)
    }

    // Handle contract data (список доступных скинов для контракта)
    if (data.contract) {
      const { setAvailableItems } = useContractStore.getState()
      setAvailableItems(data.contract)
    }
  }

  /**
   * GET request wrapper
   */
  async get(endpoint, params = {}) {
    const url = new URL(this.getApiUrl(endpoint))
    Object.keys(params).forEach(key => {
      if (params[key] !== undefined && params[key] !== null) {
        url.searchParams.append(key, params[key])
      }
    })

    return this.makeRequest(url.pathname + url.search)
  }

  /**
   * POST request wrapper
   */
  async post(endpoint, body = {}) {
    return this.makeRequest(endpoint, {
      method: 'POST',
      body: JSON.stringify(body)
    })
  }

  /**
   * POST request wrapper для form-urlencoded данных
   */
  async postForm(endpoint, body = {}) {
    const formData = new URLSearchParams()
    Object.keys(body).forEach(key => {
      if (body[key] !== undefined && body[key] !== null) {
        formData.append(key, body[key])
      }
    })

    return this.makeRequest(endpoint, {
      method: 'POST',
      body: formData.toString(),
      headers: {
        'Content-Type': 'application/x-www-form-urlencoded'
      }
    })
  }

  /**
   * PUT request wrapper
   */
  async put(endpoint, body = {}) {
    return this.makeRequest(endpoint, {
      method: 'PUT',
      body: JSON.stringify(body)
    })
  }

  /**
   * DELETE request wrapper
   */
  async delete(endpoint) {
    return this.makeRequest(endpoint, {
      method: 'DELETE'
    })
  }
}

// Create singleton instance
const apiService = new ApiService()

export default apiService
